import { useState, useEffect, useCallback } from 'react';
import { getItems, createItem, updateItem, deleteItem, STROKES } from './fitnessApi';
import { useUserProfile, getNicknameForEmail } from '../UserProfileContext';

const LIST = 'SwimmingTemplates';

function emptySet() {
  return { distance: 100, reps: 1, stroke: 'Free', notes: '', restSeconds: '', sectionHeader: '' };
}

function calcTotal(sets) {
  return sets.reduce((sum, s) => sum + (Number(s.distance) || 0) * (Number(s.reps) || 1), 0);
}

function groupBySection(sets) {
  const groups = [];
  let current = { header: '', sets: [], startIndex: 0 };
  sets.forEach((s, i) => {
    if (s.sectionHeader) {
      if (current.sets.length > 0 || current.header) groups.push(current);
      current = { header: s.sectionHeader, sets: [s], startIndex: i };
    } else {
      if (groups.length === 0 && current.sets.length === 0 && !current.header) {
        current.startIndex = i;
      }
      current.sets.push(s);
    }
  });
  if (current.sets.length > 0 || current.header) groups.push(current);
  return groups;
}

function SwimmingTemplates({ getToken, userEmail }) {
  const [allTemplates, setAllTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState('mine'); // 'mine' or 'family'
  const [nicknames, setNicknames] = useState({}); // email -> nickname cache
  const [copying, setCopying] = useState(null); // template id being copied
  const [expandedId, setExpandedId] = useState(null);

  const { nickname } = useUserProfile();

  const fetchTemplates = useCallback(async () => {
    try {
      setError(null);
      const token = await getToken();
      const items = await getItems(token, LIST);
      const mapped = items.map(it => ({
        id: it.id,
        name: it.Title || '',
        description: it.Description || '',
        sets: safeParse(it.Sets),
        totalDistance: it.TotalDistance || 0,
        createdByEmail: it.CreatedByEmail || '',
      }));
      setAllTemplates(mapped);

      // Resolve nicknames for all unique emails
      const emails = [...new Set(mapped.map(t => t.createdByEmail).filter(Boolean))];
      const resolved = {};
      for (const email of emails) {
        resolved[email] = await getNicknameForEmail(token, email);
      }
      setNicknames(resolved);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const myTemplates = allTemplates.filter(
    t => t.createdByEmail.toLowerCase() === userEmail.toLowerCase()
  );
  const familyTemplates = allTemplates.filter(
    t => t.createdByEmail.toLowerCase() !== userEmail.toLowerCase()
  );
  const displayedTemplates = viewMode === 'mine' ? myTemplates : familyTemplates;

  function startNew() {
    setEditing({ name: '', description: '', sets: [emptySet()] });
  }

  function startEdit(tmpl) {
    setEditing({ ...tmpl, sets: tmpl.sets.length ? [...tmpl.sets] : [emptySet()] });
  }

  async function handleCopy(tmpl) {
    setCopying(tmpl.id);
    setError(null);
    try {
      const token = await getToken();
      const total = calcTotal(tmpl.sets);
      await createItem(token, LIST, {
        Title: `${tmpl.name} (copy)`,
        Description: tmpl.description,
        Sets: JSON.stringify(tmpl.sets),
        TotalDistance: total,
        CreatedByEmail: userEmail,
      });
      await fetchTemplates();
      setViewMode('mine');
    } catch (err) {
      setError(err.message);
    } finally {
      setCopying(null);
    }
  }

  async function handleDelete(tmpl) {
    if (!window.confirm(`Delete template "${tmpl.name}"?`)) return;
    try {
      const token = await getToken();
      await deleteItem(token, LIST, tmpl.id);
      setAllTemplates(prev => prev.filter(t => t.id !== tmpl.id));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleSave() {
    if (!editing.name.trim()) { setError('Template name is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const token = await getToken();
      const total = calcTotal(editing.sets);
      const fields = {
        Title: editing.name.trim(),
        Description: editing.description,
        Sets: JSON.stringify(editing.sets),
        TotalDistance: total,
        CreatedByEmail: userEmail,
      };
      if (editing.id) {
        await updateItem(token, LIST, editing.id, fields);
      } else {
        await createItem(token, LIST, fields);
      }
      setEditing(null);
      await fetchTemplates();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function updateSet(index, field, value) {
    setEditing(prev => {
      const sets = [...prev.sets];
      sets[index] = { ...sets[index], [field]: value };
      return { ...prev, sets };
    });
  }

  function addSet(afterIndex) {
    setEditing(prev => {
      const sets = [...prev.sets];
      if (afterIndex !== undefined) {
        sets.splice(afterIndex + 1, 0, emptySet());
      } else {
        sets.push(emptySet());
      }
      return { ...prev, sets };
    });
  }

  function addSection() {
    const name = window.prompt('Section name (e.g. Warm-Up, Main Set, Cool Down):');
    if (!name || !name.trim()) return;
    setEditing(prev => ({
      ...prev,
      sets: [...prev.sets, { ...emptySet(), sectionHeader: name.trim() }],
    }));
  }

  function removeSet(index) {
    setEditing(prev => ({ ...prev, sets: prev.sets.filter((_, i) => i !== index) }));
  }

  function moveSet(from, to) {
    if (to < 0 || to >= editing.sets.length) return;
    setEditing(prev => {
      const sets = [...prev.sets];
      const [moved] = sets.splice(from, 1);
      sets.splice(to, 0, moved);
      return { ...prev, sets };
    });
  }

  // Drag-and-drop state
  const [dragIndex, setDragIndex] = useState(null);

  function handleDragStart(index) { setDragIndex(index); }
  function handleDragOver(e, index) {
    e.preventDefault();
    if (dragIndex !== null && dragIndex !== index) {
      moveSet(dragIndex, index);
      setDragIndex(index);
    }
  }
  function handleDragEnd() { setDragIndex(null); }

  if (loading) return <p className="Fitness-loading">Loading templates...</p>;

  // Template editor
  if (editing) {
    const total = calcTotal(editing.sets);
    const sections = groupBySection(editing.sets);
    return (
      <div className="SwimTemplates-editor">
        <h2>{editing.id ? 'Edit Template' : 'New Template'}</h2>
        {error && <p className="Fitness-error">{error}</p>}
        <input
          className="Fitness-input"
          type="text"
          placeholder="Template name"
          value={editing.name}
          onChange={e => setEditing(prev => ({ ...prev, name: e.target.value }))}
        />
        <input
          className="Fitness-input"
          type="text"
          placeholder="Description (optional)"
          value={editing.description}
          onChange={e => setEditing(prev => ({ ...prev, description: e.target.value }))}
        />
        <div className="SwimTemplates-total">Total Distance: {total}m</div>

        <div className="SwimTemplates-sets">
          {sections.map((section, si) => (
            <div key={si} className="SwimTemplates-section-group">
              {section.header && (
                <div className="SwimTemplates-section-banner">
                  <input
                    className="SwimTemplates-section-input"
                    type="text"
                    value={section.header}
                    onChange={e => {
                      const firstSetIdx = section.startIndex;
                      updateSet(firstSetIdx, 'sectionHeader', e.target.value);
                    }}
                  />
                  <span className="SwimTemplates-section-dist">{calcTotal(section.sets)}m</span>
                  <button
                    className="Fitness-btn-icon Fitness-btn-icon--delete"
                    title="Remove section header"
                    onClick={() => updateSet(section.startIndex, 'sectionHeader', '')}
                  >&times;</button>
                </div>
              )}
              {!section.header && si > 0 && (
                <div className="SwimTemplates-section-divider" />
              )}
              {section.sets.map((set, setIdx) => {
                const i = section.startIndex + setIdx;
                return (
                  <div
                    key={i}
                    className={`SwimTemplates-set${dragIndex === i ? ' SwimTemplates-set--dragging' : ''}`}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragOver={e => handleDragOver(e, i)}
                    onDragEnd={handleDragEnd}
                  >
                    <div className="SwimTemplates-set-drag">&#9776;</div>
                    <div className="SwimTemplates-set-fields">
                      <div className="SwimTemplates-set-row">
                        <label>
                          <span>Reps</span>
                          <input
                            className="Fitness-input Fitness-input--tiny"
                            type="number"
                            min="1"
                            value={set.reps}
                            onChange={e => updateSet(i, 'reps', parseInt(e.target.value) || 1)}
                          />
                        </label>
                        <span className="SwimTemplates-x">x</span>
                        <label>
                          <span>Distance</span>
                          <input
                            className="Fitness-input Fitness-input--tiny"
                            type="number"
                            min="0"
                            step="25"
                            value={set.distance}
                            onChange={e => updateSet(i, 'distance', parseInt(e.target.value) || 0)}
                          />
                        </label>
                        <label>
                          <span>Stroke</span>
                          <select
                            className="Fitness-select"
                            value={set.stroke}
                            onChange={e => updateSet(i, 'stroke', e.target.value)}
                          >
                            {STROKES.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </label>
                      </div>
                      <div className="SwimTemplates-set-row">
                        <label className="SwimTemplates-notes-label">
                          <span>Notes</span>
                          <input
                            className="Fitness-input"
                            type="text"
                            placeholder="e.g. with board, build each 50"
                            value={set.notes}
                            onChange={e => updateSet(i, 'notes', e.target.value)}
                          />
                        </label>
                        <label>
                          <span>Rest (sec)</span>
                          <input
                            className="Fitness-input Fitness-input--tiny"
                            type="number"
                            min="0"
                            step="5"
                            value={set.restSeconds}
                            onChange={e => updateSet(i, 'restSeconds', e.target.value)}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="SwimTemplates-set-actions">
                      <button className="Fitness-btn-icon" onClick={() => moveSet(i, i - 1)} disabled={i === 0} title="Move up">&#9650;</button>
                      <button className="Fitness-btn-icon" onClick={() => moveSet(i, i + 1)} disabled={i === editing.sets.length - 1} title="Move down">&#9660;</button>
                      <button className="Fitness-btn-icon Fitness-btn-icon--delete" onClick={() => removeSet(i)} title="Remove set">&times;</button>
                    </div>
                  </div>
                );
              })}
              <button
                className="Fitness-btn Fitness-btn--small Fitness-btn--secondary SwimTemplates-section-add-set"
                onClick={() => addSet(section.startIndex + section.sets.length - 1)}
              >+ Add Set</button>
            </div>
          ))}
        </div>

        <div className="SwimTemplates-editor-add-btns">
          <button className="Fitness-btn Fitness-btn--secondary" onClick={addSection}>+ Add Section</button>
        </div>

        <div className="SwimTemplates-editor-actions">
          <button className="Fitness-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Template'}
          </button>
          <button className="Fitness-btn Fitness-btn--secondary" onClick={() => setEditing(null)}>Cancel</button>
        </div>
      </div>
    );
  }

  // Template list view
  return (
    <div className="SwimTemplates">
      {error && <p className="Fitness-error">{error}</p>}

      <div className="SwimTemplates-view-toggle">
        <button
          className={`SwimTemplates-toggle-btn${viewMode === 'mine' ? ' SwimTemplates-toggle-btn--active' : ''}`}
          onClick={() => setViewMode('mine')}
        >
          My Templates ({myTemplates.length})
        </button>
        <button
          className={`SwimTemplates-toggle-btn${viewMode === 'family' ? ' SwimTemplates-toggle-btn--active' : ''}`}
          onClick={() => setViewMode('family')}
        >
          Family Templates ({familyTemplates.length})
        </button>
      </div>

      {viewMode === 'mine' && (
        <button className="Fitness-btn" onClick={startNew}>+ New Template</button>
      )}

      {displayedTemplates.length === 0 && viewMode === 'mine' && (
        <p className="Fitness-empty">No templates yet. Create one to get started.</p>
      )}
      {displayedTemplates.length === 0 && viewMode === 'family' && (
        <p className="Fitness-empty">No family templates shared yet.</p>
      )}

      <div className="SwimTemplates-list">
        {displayedTemplates.map(tmpl => {
          const sections = groupBySection(tmpl.sets);
          const isOwner = tmpl.createdByEmail.toLowerCase() === userEmail.toLowerCase();
          const creatorName = nicknames[tmpl.createdByEmail] || tmpl.createdByEmail;
          const isExpanded = expandedId === tmpl.id;
          return (
            <div key={tmpl.id} className="SwimTemplates-card">
              <div
                className="SwimTemplates-card-header"
                onClick={() => setExpandedId(isExpanded ? null : tmpl.id)}
              >
                <div className="SwimTemplates-card-header-left">
                  <h3>{tmpl.name}</h3>
                  <span className="SwimTemplates-card-meta">
                    {tmpl.totalDistance}m &middot; {tmpl.sets.length} set{tmpl.sets.length !== 1 ? 's' : ''}
                    {!isOwner && <> &middot; <em>{creatorName}</em></>}
                  </span>
                </div>
                <span className={`SwimTemplates-card-chevron${isExpanded ? ' SwimTemplates-card-chevron--open' : ''}`}>&#9660;</span>
              </div>
              {isExpanded && (
                <div className="SwimTemplates-card-body">
                  {tmpl.description && <p className="SwimTemplates-card-desc">{tmpl.description}</p>}
                  <div className="SwimTemplates-card-sections">
                    {sections.map((sec, si) => (
                      <div key={si} className="SwimTemplates-card-section">
                        {sec.header && (
                          <div className="SwimTemplates-card-section-header">
                            <span>{sec.header}</span>
                            <span className="SwimTemplates-card-section-dist">{calcTotal(sec.sets)}m</span>
                          </div>
                        )}
                        <div className="SwimTemplates-card-preview">
                          {sec.sets.map((s, i) => (
                            <span key={i} className="SwimTemplates-card-set-preview">
                              {s.reps > 1 ? `${s.reps}x` : ''}{s.distance} {s.stroke}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="SwimTemplates-card-actions">
                    {isOwner && (
                      <>
                        <button className="Fitness-btn Fitness-btn--small" onClick={() => startEdit(tmpl)}>Edit</button>
                        <button className="Fitness-btn Fitness-btn--small Fitness-btn--danger" onClick={() => handleDelete(tmpl)}>Delete</button>
                      </>
                    )}
                    {!isOwner && (
                      <button
                        className="Fitness-btn Fitness-btn--small"
                        onClick={() => handleCopy(tmpl)}
                        disabled={copying === tmpl.id}
                      >
                        {copying === tmpl.id ? 'Copying...' : 'Copy to My Templates'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function safeParse(json) {
  try { return JSON.parse(json) || []; } catch { return []; }
}

export default SwimmingTemplates;
